import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Pagination, Typography } from 'antd';

import { PageWrapper } from '@/components/Shared/Wrapper';
import themeToken from '@lib/theme/tokens/index';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import MainContainer from '@/components/Shared/MainContainer';
import MarketPlaceIcon from '@/components/Icons/MarketPlaceIcon';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';

import ListItem from '../components/ListItem';
import { MarketPlaceList } from '../components/List';
import { useGetShareSales } from '../api/getShareSales';
import SellInformationModal from '../components/SellInformationModal';
import type { MarketPlaceItem } from '../types';
import InterestedModal from '../components/InterestedModal';

const { Text } = Typography;

const MarketPlace = () => {
  const { t } = useTranslation('marketPlace');
  const { t: translate } = useTranslation('pageTitles');
  useDocumentTitle(translate('marketPlace'));
  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInterestedModalOpen, setIsInterestedModalOpen] = useState(false);
  const [selectedShare, setSelectedShare] = useState<number>();

  const [paginationParams, setPaginationParams] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const { data: shareSalesData, isLoading: isShareSalesLoading } = useGetShareSales({
    companyID: selectedCompany?.cid,
    queryParams: {
      page: paginationParams.current,
      limit: paginationParams.pageSize,
    },
  });

  const handleModalCancel = () => setIsModalOpen(false);
  const handleModalOpen = () => setIsModalOpen(true);
  const handleSelectShare = (shareID: number) => {
    setSelectedShare(shareID);
    handleModalOpen();
  };

  useEffect(() => {
    if (shareSalesData?.metadata) {
      const { total } = shareSalesData.metadata;
      setPaginationParams((prev) => ({ ...prev, total }));
    }
  }, [shareSalesData?.metadata]);

  const handleInterestedModalCancel = () => setIsInterestedModalOpen(false);
  const handleInterestedModalOpen = (_selectedShareID: number) => {
    setIsInterestedModalOpen(true);
    setSelectedShare(_selectedShareID);
  };

  return (
    <MainContainer
      title={t('shareTrading')}
      breadcrumbItems={[
        {
          title: (
            <>
              <MarketPlaceIcon width={12} height={20} />
              {t('shareTrading')}
            </>
          ),
        },
      ]}
    >
      <PageWrapper gap={16} vertical>
        <Text
          style={{
            color: themeToken['branding-primary-6'],
            fontWeight: 600,
            fontSize: themeToken.fontSizeLG,
          }}
        >
          {t('allShareActivities')}
        </Text>
        <MarketPlaceList
          dataSource={shareSalesData?.data}
          renderItem={(item: MarketPlaceItem, index: number) => (
            <ListItem
              item={item}
              handleSelectShare={handleSelectShare}
              itemIndex={index}
              pageSize={paginationParams.pageSize}
              currentPage={paginationParams.current}
              openInterestedModal={handleInterestedModalOpen}
            />
          )}
          loading={isShareSalesLoading}
        />
        <Flex justify="end" style={{ flex: 1 }} align="end">
          <Pagination
            defaultCurrent={1}
            total={paginationParams.total}
            defaultPageSize={paginationParams.pageSize}
            onChange={(page, pageSize) =>
              setPaginationParams({ current: page, pageSize, total: paginationParams.total })
            }
          />
        </Flex>
      </PageWrapper>
      {selectedShare && (
        <SellInformationModal
          isModalOpen={isModalOpen}
          handleModalCancel={handleModalCancel}
          selectedShare={selectedShare}
        />
      )}

      <InterestedModal
        isModalOpen={isInterestedModalOpen}
        handleModalCancel={handleInterestedModalCancel}
        selectedShareID={selectedShare}
        selectedCompanyID={selectedCompany?.cid}
        originalSharePrice={shareSalesData?.data.find((share) => share.id === selectedShare)?.share_price as number}
      />
    </MainContainer>
  );
};

export default MarketPlace;
